import { TransactionType } from 'graphql'
import { TransactionFormState } from '..'
import { TransactionItemFormState } from '../components/AddPartsForm'
import {
	validateFromId,
	validateToId,
} from '../../../../../graphql/utils/transaction-types'

import { compareCost, CostComparison } from 'views/helpers/utils'

export type SetUnexpectedCost = ( index: number, flag: CostComparison ) => void

export function validateQuantity( quantity: string, availableQuantity?: number ) {
	const intQuantity = parseInt( quantity, 10 )
	if ( !Number.isFinite( intQuantity ) ) {
		return {
			isInvalid: true,
			error: 'Quantity is required'
		}
	}
	if ( intQuantity < 1 ) {
		return {
			isInvalid: true,
			error: 'Quantity must be greater than zero'
		}
	}
	if ( availableQuantity !== undefined && intQuantity > availableQuantity ) {
		return {
			isInvalid: true,
			error: `Quantity exceeds available (${availableQuantity})`
		}
	}

	return { isInvalid: false }
}

export function checkForUnexpectedCost( item: TransactionItemFormState, associatedStorePart: { cost?: number | null | undefined } | null | undefined, setUnexpectedCost: SetUnexpectedCost ) {
	const floatCost = parseFloat( item.costPerItem )
	const expectedCost = item.storePart?.cost ?? associatedStorePart?.cost ?? undefined
	let comparison: CostComparison = 'normal'
	let validation: { isInvalid: boolean, error?: string } = { isInvalid: false }

	if ( Number.isFinite( floatCost ) ) {
		comparison = compareCost( floatCost, expectedCost )

		if ( comparison === 'low' ) {
			validation = {
				isInvalid: true,
				error: 'Low price',
			}
		} else if ( comparison === 'high' ) {
			validation = {
				isInvalid: true,
				error: 'High price',
			}
		}
	}

	setUnexpectedCost( item.idx, comparison )
	return validation
}

export function validateCostPerItem( cost: string ) {
	const floatCost = parseFloat( cost )
	if ( !Number.isFinite( floatCost ) ) {
		return {
			isInvalid: true,
			error: 'Cost is required'
		}
	}
	return { isInvalid: false }
}

export function validateItem( item: TransactionItemFormState ) {
	const newItemState = { ...item }
	let hasErrors = false

	if ( item.part || item.storePart ) {
		newItemState.validation.quantity = validateQuantity( item.quantity, item.storePart?.availableQuantity )
		newItemState.validation.costPerItem = validateCostPerItem( item.costPerItem )
		hasErrors = newItemState.validation.quantity.isInvalid || newItemState.validation.costPerItem.isInvalid
	} else {
		newItemState.validation.part = {
			isInvalid: true,
			error: 'You must select an item'
		}
		hasErrors = true
	}

	return { hasErrors, newItemState }
}

export default function validateForm( formState: TransactionFormState, transactionType: TransactionType,	fromId?: number, toId?: number ) {
	const { transactionItems } = formState

	const newFormState = { ...formState }
	let hasErrors = false

	if ( !validateFromId( transactionType, fromId ) ) {
		newFormState.validation.fromEntity = {
			isInvalid: true,
			error: 'You must select where the items are coming from',
		}

		hasErrors = true
	}

	if ( !validateToId( transactionType, toId ) ) {
		newFormState.validation.toEntity = {
			isInvalid: true,
			error: 'You must select where the items are going to'
		}

		hasErrors = true
	}

	if ( hasErrors ) {
		return { hasErrors, newFormState }
	}

	if ( !transactionItems.length ) {
		newFormState.validation.items = {
			isInvalid: true,
			error: 'You must select at least one item'
		}
		hasErrors = true
	}

	transactionItems.forEach( ( item, index ) => {
		const { newItemState, hasErrors: itemHasErrors } = validateItem( item )
		if ( itemHasErrors ) {
			newFormState.transactionItems[ index ] = newItemState
			hasErrors = true
		}
	} )

	return { hasErrors, newFormState }
}
